import { OpenInNew } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/AddOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { setError, setOk, setInfo } from 'client/modules/App/AppActions';
import {
  checkSalesforcePackageStatus,
  getIntegration,
  upgradeSalesforcePackage,
  installSalesforcePackage,
  checkSalesforceInstallationStatus,
} from 'client/modules/Integrations/IntegrationsActions';
import styles from 'client/modules/Settings/pages/IntegrationsPage/IntegrationsListPage.css';
import { getSurveys } from 'client/modules/Surveys/SurveyReducer';
import useOpenClose from 'client/util/useOpenClose';
import React from 'react';
import { IoTrashOutline as DeleteIcon, IoPencil as EditIcon } from 'react-icons/io5';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router';
import Config from '../../../../../../server/config';
import SalesforceFetchPropertiesModal from './SalesforceFetchPropertiesModal';
import TriggersModal from './TriggersModal';
import { useFindFoldersQuery } from 'client/modules/Surveys/folderApiSlice';

const messages = defineMessages({
  trigger: {
    id: 'IntegrationConfigSalesforce.Trigger',
    defaultMessage: 'Trigger',
  },
  delay: {
    id: 'IntegrationConfigSalesforce.Delay',
    defaultMessage: 'Send survey after',
  },
  createdCase: {
    id: 'IntegrationConfigSalesforce.CreatedCase',
    defaultMessage: 'Case created',
  },
  closedCase: {
    id: 'IntegrationConfigSalesforce.ClosedCase',
    defaultMessage: 'Case closed',
  },
  createdOpportunity: {
    id: 'IntegrationConfigSalesforce.CreatedOpportunity',
    defaultMessage: 'Opportunity created',
  },
  wonOpportunity: {
    id: 'IntegrationConfigSalesforce.WonOpportunity',
    defaultMessage: 'Opportunity won',
  },
  lostOpportunity: {
    id: 'IntegrationConfigSalesforce.LostOpportunity',
    defaultMessage: 'Opportunity lost',
  },
  Custom: {
    id: 'IntegrationConfigSalesforce.Custom',
    defaultMessage: 'Custom',
  },
  Immediately: {
    id: 'IntegrationConfigSalesforce.Immediately',
    defaultMessage: 'Immediately',
  },
  '1 Hour': {
    id: 'IntegrationConfigSalesforce.1Hour',
    defaultMessage: '1 Hour',
  },
  '1 Day': {
    id: 'IntegrationConfigSalesforce.1Day',
    defaultMessage: '1 Day',
  },
  '1 Week': {
    id: 'IntegrationConfigSalesforce.1Week',
    defaultMessage: '1 Week',
  },
  noTriggers: {
    id: 'IntegrationConfigSalesforce.NoTriggers',
    defaultMessage: 'No triggers',
  },
  outboundMessageUrl: {
    id: 'IntegrationConfigSalesforce.OutboundMessageUrl',
    defaultMessage: 'The target URL for your Salesforce outbound message.',
  },
  pushApiTriggerUrl: {
    id: 'IntegrationConfigSalesforce.PushApiTriggerUrl',
    defaultMessage: 'Push API: POST JSON with email and properties to this URL.',
  },
  syncResponses: {
    id: 'IntegrationsSettings.SalesforceSyncResponses',
    defaultMessage: 'Sync responses to contacts',
  },
  syncResponsesDescription: {
    id: 'IntegrationsSettings.SalesforceSyncResponsesDescription',
    defaultMessage:
      'Every time a response is created, we will update the Salesforce contact matching the response email with the latest survey data.',
  },
  addSyncSurvey: {
    id: 'IntegrationsSettings.SalesforceAddSyncSurvey',
    defaultMessage: 'Add survey to sync',
  },
  survey: {
    id: 'IntegrationsSettings.SalesforceSurvey',
    defaultMessage: 'Survey',
  },
  surveys: {
    id: 'IntegrationsSettings.SalesforceSurveys',
    defaultMessage: 'Surveys',
  },
  remove: {
    id: 'IntegrationsSettings.Remove',
    defaultMessage: 'Remove',
  },
  noSurveys: {
    id: 'IntegrationsSettings.noSurveys',
    defaultMessage: 'No surveys to sync',
  },
  triggersSection: {
    id: 'IntegrationsSettings.SalesforceTriggersSection',
    defaultMessage: 'Triggers',
  },
  addTrigger: {
    id: 'IntegrationsSettings.SalesforceAddTrigger',
    defaultMessage: 'Add Trigger',
  },
  edit: {
    id: 'IntegrationsSettings.Edit',
    defaultMessage: 'Edit',
  },
  CustomTriggerDescription: {
    id: 'IntegrationConfigSalesforce.CustomTriggerDescription',
    defaultMessage:
      'Establish personalized triggers in Salesforce for specific entity categories such as Account, Case, Contact, and Opportunity by following the guidelines outlined in our documentation.',
  },
  cancel: {
    id: 'Confirm.Cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 'Confirm.Save',
    defaultMessage: 'Save',
  },
  TriggersTitle: {
    id: 'IntegrationConfigSalesforce.TriggersTitle',
    defaultMessage: 'Send surveys using Salesforce Triggers',
  },
  Documentation: {
    id: 'IntegrationConfigSalesforce.Documentation',
    defaultMessage: 'Learn more',
  },
  EnableTriggers: {
    id: 'IntegrationConfigSalesforce.EnableTriggers',
    defaultMessage: 'Enable Salesforce Triggers',
  },
  TokenExpired: {
    id: 'IntegrationConfigSalesforce.TokenExpired',
    defaultMessage:
      'Your Salesforce connection has expired. Please reconnect to enable package upgrade and additional question syncing.',
  },
  Reconnect: {
    id: 'IntegrationConfigSalesforce.Reconnect',
    defaultMessage: 'Reconnect',
  },
  UpgradeSuccess: {
    id: 'IntegrationConfigSalesforce.UpgradeSuccess',
    defaultMessage: 'Package upgrade initiated successfully.',
  },
  UpgradeError: {
    id: 'IntegrationConfigSalesforce.UpgradeError',
    defaultMessage: 'Failed to upgrade package. Please try again.',
  },
  TokenNotAvailable: {
    id: 'IntegrationConfigSalesforce.TokenNotAvailable',
    defaultMessage: 'Authentication token not available. Please refresh the page and try again.',
  },
  ReconnectError: {
    id: 'IntegrationConfigSalesforce.ReconnectError',
    defaultMessage: 'Failed to reconnect. Please try again.',
  },
  InstallSuccess: {
    id: 'IntegrationConfigSalesforce.InstallSuccess',
    defaultMessage: 'Package installation initiated successfully.',
  },
  InstallError: {
    id: 'IntegrationConfigSalesforce.InstallError',
    defaultMessage: 'Failed to install package. Please try again.',
  },
  Installing: {
    id: 'IntegrationConfigSalesforce.Installing',
    defaultMessage: 'Installing...',
  },
  InstallNow: {
    id: 'IntegrationConfigSalesforce.InstallNow',
    defaultMessage: 'Install now',
  },
  InstallManually: {
    id: 'IntegrationConfigSalesforce.InstallManually',
    defaultMessage: 'install manually',
  },
  TokenExpiredInstall: {
    id: 'IntegrationConfigSalesforce.TokenExpiredInstall',
    defaultMessage:
      'Your Salesforce connection has expired. Please reconnect to enable package upgrade and additional question syncing.',
  },
  PackageDeprecated: {
    id: 'IntegrationConfigSalesforce.PackageDeprecated',
    defaultMessage:
      'This version of the Promoter Ninja package is deprecated. Please upgrade to the latest version to continue using this integration.',
  },
  InstallationInProgress: {
    id: 'IntegrationConfigSalesforce.InstallationInProgress',
    defaultMessage:
      'Installation in progress. Please wait while we verify the installation status...',
  },
  InstallationStatusChecking: {
    id: 'IntegrationConfigSalesforce.InstallationStatusChecking',
    defaultMessage: 'Checking installation status...',
  },
  InstallationCompleted: {
    id: 'IntegrationConfigSalesforce.InstallationCompleted',
    defaultMessage: 'Package installation completed successfully!',
  },
  InstallationFailed: {
    id: 'IntegrationConfigSalesforce.InstallationFailed',
    defaultMessage: 'Package installation failed. Please try again or install manually.',
  },
  FetchPropertiesEnabled: {
    id: 'IntegrationsSettings.SalesforceFetchPropertiesEnabled',
    defaultMessage: 'Fetch properties from Salesforce',
  },
  FetchPropertiesEnabledDescription: {
    id: 'IntegrationsSettings.SalesforceFetchPropertiesEnabledDescription',
    defaultMessage: 'Fetch and store additional properties from Salesforce contacts.',
  },
  EditFetchProperties: {
    id: 'IntegrationsSettings.SalesforceEditFetchProperties',
    defaultMessage: 'Edit Properties to Fetch',
  },
  FetchPropertiesCount: {
    id: 'IntegrationsSettings.SalesforceFetchPropertiesCount',
    defaultMessage:
      '{count, plural, =0 {No properties configured} one {# property} other {# properties}}',
  },
  DataFreshness: {
    id: 'IntegrationsSettings.SalesforceDataFreshness',
    defaultMessage: 'Data freshness',
  },
  DataFreshnessDescription: {
    id: 'IntegrationsSettings.SalesforceDataFreshnessDescription',
    defaultMessage:
      'How long fetched or pushed properties are considered fresh. While fresh, the integration API will not be called.',
  },
});

const objectEvents = {
  Case: ['created', 'closed'],
  Opportunity: ['created', 'won', 'lost'],
  Custom: [''],
};
const triggerTypes = Object.keys(objectEvents)
  .map(object =>
    objectEvents[object].map(event => ({
      value: [object, event].filter(v => v).join(' '),
      label: `${event}${object}`,
    })),
  )
  .flat();
const delayTypes = ['Immediately', '1 Hour', '1 Day', '1 Week'];

function IntegrationConfigSalesforce() {
  const { formatMessage } = useIntl();
  const { integration, onIntegrationsPropertiesChange } = useOutletContext();
  const {
    instanceUrl,
    notificationToken,
    syncResponses = false,
    syncSurveys = [],
    triggers: integrationTriggers = [],
    triggersEnabled = true,
    fetchPropertiesEnabled = false,
    fetchProperties = {},
    dataFreshness = 3600,
  } = integration || {};
  const surveys = useSelector(getSurveys);
  const dispatch = useDispatch();
  const { data: folders = [] } = useFindFoldersQuery();

  const foldersMap = React.useMemo(
    () => folders.reduce((acc, curr) => ({ ...acc, [curr._id]: curr.name }), {}),
    [folders],
  );

  const surveyOptions = React.useMemo(
    () =>
      surveys
        .filter(s => !s.archived)
        .map(s => ({
          value: s.id,
          folder: s.folder, // Keep folder ID, not name
          folderName: foldersMap[s.folder] || '', // Add folder name for sorting
          name: s.name || s.brand,
        }))
        .sort((a, b) => a.folderName.localeCompare(b.folderName) || a.name.localeCompare(b.name)),
    [foldersMap, surveys],
  );
  const [triggers, setTriggers] = React.useState([]);
  const [packageVersion, setPackageVersion] = React.useState({
    loading: true,
    needsUpgrade: false,
  });
  const [upgrading, setUpgrading] = React.useState(false);
  const [installing, setInstalling] = React.useState(false);
  const [isTriggersModalOpen, openTriggersModal, closeTriggersModal, { trigger: editingTrigger }] =
    useOpenClose();
  const [isFetchModalOpen, openFetchModal, closeFetchModal] = useOpenClose();
  const [host] = instanceUrl && new URL(instanceUrl).host.split('.');

  React.useEffect(() => {
    // Initialize triggers from integration on mount or integration change
    setTriggers(Array.isArray(integrationTriggers) ? integrationTriggers : []);
  }, [integrationTriggers]);

  // Check package version when component mounts
  React.useEffect(() => {
    if (integration && instanceUrl) {
      dispatch(checkSalesforcePackageStatus())
        .then(data => {
          setPackageVersion({
            loading: false,
            needsUpgrade: data.needsUpgrade,
            needsInstallation: data.needsInstallation,
            tokenExpired: data.tokenExpired,
            packageInstalled: data.packageInstalled,
          });
        })
        .catch(error => {
          // Handle token expiry for version check
          if (error.response?.status === 403 && error.bodyJson?.requiresReconnection) {
            setPackageVersion({ loading: false, needsUpgrade: false, tokenExpired: true });
          } else {
            setPackageVersion({ loading: false, needsUpgrade: false });
          }
        });
    }
  }, [integration, instanceUrl, dispatch]);

  // Handler for toggling syncResponses
  const onToggleSyncResponses = event => {
    onIntegrationsPropertiesChange(event, 'salesforce', { syncResponses: event.target.checked });
    if (event.target.checked && !syncSurveys?.length && surveys.length) {
      onIntegrationsPropertiesChange(null, 'salesforce', {
        syncSurveys: [surveys[0].id],
      });
    }
  };

  // Handler for saving triggers from modal
  const handleTriggersSave = updatedTrigger => {
    let newTriggers;
    if (updatedTrigger._id) {
      // Editing existing trigger - find by _id
      newTriggers = triggers.map(t => (t._id === updatedTrigger._id ? updatedTrigger : t));
    } else {
      // Adding new trigger - generate temporary _id
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      newTriggers = [...triggers, { ...updatedTrigger, _id: tempId }];
    }
    setTriggers(newTriggers);
    onIntegrationsPropertiesChange(null, 'salesforce', { triggers: newTriggers });
  };

  // Handler for deleting a trigger
  const handleDeleteTrigger = triggerId => {
    const newTriggers = triggers.filter(t => t._id !== triggerId);
    setTriggers(newTriggers);
    onIntegrationsPropertiesChange(null, 'salesforce', { triggers: newTriggers });
  };

  // Handler for saving fetch properties
  const handleFetchPropertiesSave = fetchPropertiesConfig => {
    onIntegrationsPropertiesChange(null, 'salesforce', {
      fetchProperties: fetchPropertiesConfig,
    });
  };

  // Handler for reconnecting Salesforce
  const handleReconnectSalesforce = async () => {
    try {
      // Start the OAuth flow without removing the existing integration
      // The OAuth callback will update the integration while preserving existing configuration
      const result = await dispatch(getIntegration(null, 'salesforce', {}));
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (_error) {
      dispatch(setError(formatMessage(messages.ReconnectError)));
    }
  };

  // Handler for upgrading package
  const handleUpgradePackage = async () => {
    setUpgrading(true);
    try {
      const data = await dispatch(upgradeSalesforcePackage());

      if (data.success) {
        // Refresh package version status after a short delay
        setTimeout(() => {
          dispatch(checkSalesforcePackageStatus())
            .then(versionData => {
              setPackageVersion({
                loading: false,
                needsUpgrade: versionData.needsUpgrade,
                needsInstallation: versionData.needsInstallation,
                tokenExpired: versionData.tokenExpired,
                packageInstalled: versionData.packageInstalled,
              });
            })
            .catch(() => {
              setPackageVersion({ loading: false, needsUpgrade: false });
            });
        }, 3000); // Check again after 3 seconds

        dispatch(setOk(formatMessage(messages.UpgradeSuccess), 5000));
      } else if (data.requiresReconnection) {
        // Handle token expiry for upgrade
        setPackageVersion({
          loading: false,
          needsUpgrade: true,
          tokenExpired: true,
        });
      } else {
        dispatch(setError(formatMessage(messages.UpgradeError)));
      }
    } catch (_error) {
      dispatch(setError(formatMessage(messages.UpgradeError)));
    } finally {
      setUpgrading(false);
    }
  };

  // Handler for installing package
  const handleInstallPackage = async () => {
    setInstalling(true);

    try {
      // Step 1: Initiate the installation
      const data = await dispatch(installSalesforcePackage());

      if (data.success && data.installRequestId) {
        // Show initial progress message
        dispatch(setInfo(formatMessage(messages.InstallationInProgress), 4000));

        // Step 2: Poll for installation status
        const checkInstallStatus = async (attempt = 1, maxAttempts = 50) => {
          try {
            dispatch(setInfo(formatMessage(messages.InstallationStatusChecking), 4000));

            const statusResult = await dispatch(
              checkSalesforceInstallationStatus(null, data.installRequestId),
            );

            if (statusResult.status === 'IN_PROGRESS') {
              // Still installing, check again after delay
              if (attempt < maxAttempts) {
                setTimeout(() => checkInstallStatus(attempt + 1, maxAttempts), 3000);
              } else {
                // Max attempts reached, show timeout message
                dispatch(setError(formatMessage(messages.InstallationFailed)));
              }
            } else if (statusResult.status === 'SUCCESS') {
              // Installation completed successfully
              dispatch(setOk(formatMessage(messages.InstallationCompleted), 5000));

              // Update package version state
              setPackageVersion({
                loading: false,
                needsUpgrade: false,
                needsInstallation: false,
                tokenExpired: false,
                packageInstalled: true,
              });
            } else if (statusResult.status === 'ERROR') {
              // Installation failed - check if it's due to package deprecation
              if (statusResult.packageDeprecated) {
                dispatch(setError(formatMessage(messages.PackageDeprecated)));
              } else {
                dispatch(setError(formatMessage(messages.InstallationFailed)));
              }
            } else {
              // Unknown status, show generic error
              dispatch(setError(formatMessage(messages.InstallationFailed)));
            }
          } catch (_statusError) {
            dispatch(setError(formatMessage(messages.InstallationFailed)));
          }
        };

        // Start checking status after a 2-second delay
        setTimeout(() => checkInstallStatus(), 2000);
      } else if (data.requiresReconnection) {
        dispatch(setError(formatMessage(messages.TokenExpiredInstall)));
      } else if (data.packageDeprecated) {
        dispatch(setError(formatMessage(messages.PackageDeprecated)));
      } else {
        dispatch(setError(formatMessage(messages.InstallError)));
      }
    } catch (error) {
      if (error.response?.data?.requiresReconnection) {
        dispatch(setError(formatMessage(messages.TokenExpiredInstall)));
      } else if (error.response?.data?.packageDeprecated) {
        dispatch(setError(formatMessage(messages.PackageDeprecated)));
      } else {
        dispatch(setError(formatMessage(messages.InstallError)));
      }
    } finally {
      setInstalling(false);
    }
  };

  // Helper: folder id to name map
  const folderMap = React.useMemo(() => {
    return (folders || []).reduce((acc, f) => {
      acc[f._id] = f.name;
      return acc;
    }, {});
  }, [folders]);

  // Helper: get label for survey (with folder if >1 folder)
  const getSurveyLabel = survey => {
    if (!survey) return '';
    if (folders.length > 1) {
      const folderName = survey.folder ? folderMap[survey.folder] : '';
      const folderLabel =
        folderName || formatMessage({ id: 'Surveys.NoFolder', defaultMessage: 'No Folder' });
      return (
        <ul className={styles.surveyBreadcrumb}>
          <li>{folderLabel}</li>
          <li>{survey.name || survey.brand || ''}</li>
        </ul>
      );
    }
    return (
      <ul className={styles.surveyBreadcrumb}>
        <li>{survey.name || survey.brand || ''}</li>
      </ul>
    );
  };

  // Prepare sorted triggers array
  const sortedTriggers = React.useMemo(() => {
    return triggers.slice().sort((a, b) => {
      const surveyA = surveyOptions.find(s => s.value === a.survey);
      const surveyB = surveyOptions.find(s => s.value === b.survey);
      const folderA = surveyA?.folder ? folderMap[surveyA.folder] : '';
      const folderB = surveyB?.folder ? folderMap[surveyB.folder] : '';
      const folderLabelA =
        folderA || formatMessage({ id: 'Surveys.NoFolder', defaultMessage: 'No Folder' });
      const folderLabelB =
        folderB || formatMessage({ id: 'Surveys.NoFolder', defaultMessage: 'No Folder' });
      if (folderLabelA !== folderLabelB) {
        return folderLabelA.localeCompare(folderLabelB);
      }
      const nameA = (surveyA?.name || surveyA?.brand || '').toLowerCase();
      const nameB = (surveyB?.name || surveyB?.brand || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [triggers, surveyOptions, folderMap, formatMessage]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography sx={{ gap: 1, display: 'inline-flex' }}>
          Site:
          <a href={instanceUrl} target="_blank" rel="noreferrer">
            {host}
            <OpenInNew sx={{ fontSize: 16, verticalAlign: 'middle', ml: 0.5 }} />
          </a>
        </Typography>
        <Button
          variant="text"
          onClick={handleReconnectSalesforce}
          color="muted"
          size="small"
          sx={{ ml: 1, textTransform: 'none' }}
        >
          <FormattedMessage
            id="IntegrationConfigSalesforce.Reauthorize"
            defaultMessage="Reauthorize"
          />
        </Button>
      </Box>

      {!packageVersion.loading && packageVersion.needsInstallation && (
        <Alert severity="warning" sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2">
              <FormattedMessage
                id="IntegrationConfigSalesforce.PackageNotInstalled"
                defaultMessage="Promoter Ninja package must be installed in Salesforce to enable this integration."
              />
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleInstallPackage}
                disabled={installing}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {installing ? (
                  <FormattedMessage
                    id="IntegrationConfigSalesforce.Installing"
                    defaultMessage="Installing..."
                  />
                ) : (
                  <FormattedMessage
                    id="IntegrationConfigSalesforce.InstallNow"
                    defaultMessage="Install now"
                  />
                )}
              </Button>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <FormattedMessage id="IntegrationConfigSalesforce.Or" defaultMessage="or" />
              </Typography>
              <a
                href={`https://login.salesforce.com/packaging/installPackage.apexp?p0=${Config.SalesForceManagedAppVersion}`}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: '0.875rem' }}
              >
                <FormattedMessage
                  id="IntegrationConfigSalesforce.InstallManually"
                  defaultMessage="install manually"
                />
              </a>
            </Box>
          </Box>
        </Alert>
      )}

      {!packageVersion.loading &&
        packageVersion.needsUpgrade &&
        !packageVersion.needsInstallation && (
          <Alert severity="info" sx={{ width: '100%', mt: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography variant="body2">
                <FormattedMessage
                  id="IntegrationConfigSalesforce.PackageUpgradeNeeded"
                  defaultMessage="To sync additional question responses, please upgrade your Promoter Ninja package to the latest version."
                />
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUpgradePackage}
                  disabled={upgrading}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {upgrading ? (
                    <FormattedMessage
                      id="IntegrationConfigSalesforce.Upgrading"
                      defaultMessage="Upgrading..."
                    />
                  ) : (
                    <FormattedMessage
                      id="IntegrationConfigSalesforce.UpgradeNow"
                      defaultMessage="Upgrade now"
                    />
                  )}
                </Button>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <FormattedMessage id="IntegrationConfigSalesforce.Or" defaultMessage="or" />
                </Typography>
                <a
                  href={`https://login.salesforce.com/packaging/installPackage.apexp?p0=${Config.SalesForceManagedAppVersion}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: '0.875rem' }}
                >
                  <FormattedMessage
                    id="IntegrationConfigSalesforce.UpgradeManually"
                    defaultMessage="upgrade manually"
                  />
                </a>
              </Box>
            </Box>
          </Alert>
        )}

      {!packageVersion.loading && packageVersion.tokenExpired && (
        <Alert severity="warning" sx={{ width: '100%', mt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2">
              <FormattedMessage
                id="IntegrationConfigSalesforce.TokenExpired"
                defaultMessage="Your Salesforce connection has expired. Please reconnect to enable package upgrade and additional question syncing."
              />
            </Typography>
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={handleReconnectSalesforce}
              sx={{ whiteSpace: 'nowrap' }}
            >
              <FormattedMessage
                id="IntegrationConfigSalesforce.Reconnect"
                defaultMessage="Reconnect"
              />
            </Button>
          </Box>
        </Alert>
      )}

      {/* Sync Responses Section */}
      <Box>
        <Box className={styles.syncSurveysDivider} />

        <FormControlLabel
          control={
            <Checkbox checked={syncResponses} onChange={onToggleSyncResponses} color="primary" />
          }
          label={<Box sx={{ fontWeight: 600 }}>{formatMessage(messages.syncResponses)}</Box>}
          className={styles.syncSurveysCheckboxLabel}
        />
        <div className={styles.syncSurveysCheckboxDescription}>
          {formatMessage(messages.syncResponsesDescription)}
        </div>
        {(!syncResponses && !syncSurveys?.length) || (
          <Box
            className={
              styles.syncSurveysSection +
              (!syncResponses ? ` ${styles.syncSurveysSectionDisabled}` : '')
            }
          >
            <Autocomplete
              multiple
              options={surveyOptions}
              groupBy={option =>
                option.folderName ||
                formatMessage({ id: 'Surveys.NoFolder', defaultMessage: 'No Folder' })
              }
              getOptionLabel={option => option.name}
              value={surveyOptions.filter(opt => syncSurveys.includes(opt.value))}
              onChange={(_, newValue) => {
                onIntegrationsPropertiesChange(null, 'salesforce', {
                  syncSurveys: newValue.map(opt => opt.value),
                });
              }}
              disableCloseOnSelect
              renderInput={params => (
                <TextField
                  {...params}
                  variant="standard"
                  label={formatMessage(messages.surveys)}
                  fullWidth
                />
              )}
              disabled={!syncResponses}
              sx={{ mb: 2 }}
              fullWidth
            />
          </Box>
        )}
        <Box className={styles.syncSurveysDivider} />
      </Box>
      {/* End Sync Responses Section */}

      {/* Fetch Properties Section */}
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={fetchPropertiesEnabled}
              onChange={e =>
                onIntegrationsPropertiesChange(e, 'salesforce', {
                  fetchPropertiesEnabled: e.target.checked,
                })
              }
              color="primary"
            />
          }
          label={
            <Box sx={{ fontWeight: 600 }}>{formatMessage(messages.FetchPropertiesEnabled)}</Box>
          }
          className={styles.syncSurveysCheckboxLabel}
        />
        <Box className={styles.syncSurveysCheckboxDescription}>
          {formatMessage(messages.FetchPropertiesEnabledDescription)}
        </Box>
        {fetchPropertiesEnabled && (
          <>
            <Box className={styles.fetchPropertiesSection}>
              <Box>
                {formatMessage(messages.FetchPropertiesCount, {
                  count: Object.keys(fetchProperties).length,
                })}
              </Box>
              <IconButton
                color="primary"
                size="small"
                onClick={openFetchModal}
                aria-label={formatMessage(messages.edit)}
                title={formatMessage(messages.edit)}
              >
                <EditIcon />
              </IconButton>
            </Box>
            <Box sx={{ mt: 2, mb: 1 }}>
              <TextField
                select
                label={formatMessage(messages.DataFreshness)}
                value={dataFreshness}
                onChange={e =>
                  onIntegrationsPropertiesChange(null, 'salesforce', {
                    dataFreshness: Number(e.target.value),
                  })
                }
                size="small"
                sx={{ minWidth: 200 }}
                helperText={formatMessage(messages.DataFreshnessDescription)}
              >
                <MenuItem value={900}>15 minutes</MenuItem>
                <MenuItem value={3600}>1 hour</MenuItem>
                <MenuItem value={14400}>4 hours</MenuItem>
                <MenuItem value={43200}>12 hours</MenuItem>
                <MenuItem value={86400}>24 hours</MenuItem>
                <MenuItem value={604800}>7 days</MenuItem>
              </TextField>
            </Box>
          </>
        )}
        <Box className={styles.syncSurveysDivider} />
      </Box>
      {/* End Fetch Properties Section */}

      {/* Triggers Section */}
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={triggersEnabled}
              onChange={e =>
                onIntegrationsPropertiesChange(null, 'salesforce', {
                  triggersEnabled: e.target.checked,
                })
              }
              color="primary"
            />
          }
          label={<Box sx={{ fontWeight: 600 }}>{formatMessage(messages.TriggersTitle)}</Box>}
          className={styles.syncSurveysCheckboxLabel}
        />
        <div className={styles.syncSurveysCheckboxDescription}>
          <FormattedMessage
            id="IntegrationConfigSalesforce.TriggersDescription"
            defaultMessage="You can automatically send surveys when cases are created/closed, opportunities are won/lost, or create custom triggers. {documentationLink}"
            values={{
              documentationLink: (
                <a
                  href="https://www.promoter.ninja/help/integrations/salesforce-integration"
                  target="_blank"
                  rel="noreferrer"
                >
                  {formatMessage(messages.Documentation)}
                </a>
              ),
            }}
          />
        </div>
        {(!triggersEnabled && triggers.length === 0) || (
          <Box
            className={[
              styles.syncSurveysSection,
              !triggersEnabled && styles.syncSurveysSectionDisabled,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <Box className={styles.syncSurveysAddBtn}>
              <Button
                variant="text"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => openTriggersModal({ trigger: undefined })}
                className={styles.syncSurveysAddBtnInner}
              >
                {formatMessage(messages.addTrigger)}
              </Button>
            </Box>
            <Card variant="outlined" className={styles.syncSurveysTable}>
              <Box className={styles.syncSurveysHeader}>
                <Box className={styles.syncSurveysHeaderSurvey}>
                  {formatMessage(messages.survey)}
                </Box>
                <Box className={styles.syncSurveysHeaderTrigger}>
                  {formatMessage(messages.trigger)}
                </Box>
                <Box className={styles.syncSurveysHeaderDelay}>{formatMessage(messages.delay)}</Box>
                <Box className={styles.syncSurveysHeaderActions} />
              </Box>
              {triggers.length === 0 ? (
                <Box className={styles.syncSurveysEmpty}>{formatMessage(messages.noTriggers)}</Box>
              ) : (
                sortedTriggers.map((trigger, sortedIdx) => {
                  const survey = surveyOptions.find(s => s.value === trigger.survey);
                  const triggerLabel = triggerTypes.find(t => t.value === trigger.trigger)?.label;
                  const delayLabel = delayTypes.includes(trigger.delay)
                    ? formatMessage(messages[trigger.delay] || { defaultMessage: trigger.delay })
                    : trigger.delay;
                  return (
                    <Box key={trigger._id || sortedIdx} className={styles.syncSurveysRow}>
                      <Box className={styles.syncSurveysRowSurvey}>{getSurveyLabel(survey)}</Box>
                      <Box className={styles.syncSurveysRowTrigger}>
                        {messages[triggerLabel]
                          ? formatMessage(messages[triggerLabel])
                          : triggerLabel}
                      </Box>
                      <Box className={styles.syncSurveysRowDelay}>{delayLabel}</Box>
                      <Box className={styles.syncSurveysRowActions}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            openTriggersModal({ trigger });
                          }}
                          aria-label={formatMessage(messages.edit)}
                          title={formatMessage(messages.edit)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteTrigger(trigger._id);
                          }}
                          aria-label={formatMessage(messages.remove)}
                          title={formatMessage(messages.remove)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Card>
          </Box>
        )}
      </Box>

      <TriggersModal
        open={isTriggersModalOpen}
        onClose={closeTriggersModal}
        trigger={editingTrigger}
        onSave={handleTriggersSave}
        surveyOptions={surveyOptions}
        triggerTypes={triggerTypes}
        delayTypes={delayTypes}
        messages={messages}
        notificationToken={notificationToken}
        configRootUrl={Config.rootUrl}
      />
      <SalesforceFetchPropertiesModal
        open={isFetchModalOpen}
        onClose={closeFetchModal}
        fetchProperties={fetchProperties}
        onSave={handleFetchPropertiesSave}
      />
      {/* End Triggers Section */}
      <Box className={styles.syncSurveysDivider} />
    </Box>
  );
}

export default IntegrationConfigSalesforce;
